// CareLogix™ ORACLE — Constitutional Gate Orchestrator (server-backed v1).
//
// 3-gate sequence:
//   1) CareLogix Privacy & Safety Foundation        (localStorage only)
//   2) ORACLE ACKNOWLEDGEMENT GATE™ V3              (server + localStorage, versioned)
//   3) Membership Selection                         (localStorage)
//
// V3 is now backed by Mongo collection `oracle_acknowledgements` via the
// `/api/oracle/acknowledgement/*` router. The orchestrator:
//   • runs the localStorage fast-path on first render (zero flash for known users)
//   • async-validates against the server immediately
//   • if the server reports a version drift (ORACLE_CURRENT_VERSION changed),
//     it clears the local ack flag and re-displays Gate 2.
//
// Additive only — no existing routes/features removed.
import React, { useCallback, useEffect, useState } from "react";

import CareLogixPrivacyGate, { isPrivacyAcknowledged } from "@/components/gate/CareLogixPrivacyGate";
import OracleAcknowledgementGateV3 from "@/components/gate/OracleAcknowledgementGateV3";
import MembershipSelectionGate, { getMembershipIntent } from "@/components/gate/MembershipSelectionGate";
import { useOracleMemberSession } from "@/hooks/useOracleMemberSession";
import {
  checkAcknowledgement,
  isAcknowledgedV3Local,
  setAcknowledgedV3Local,
  RECHECK_INTERVAL_MS,
} from "@/api/oracleAcknowledgementClient";

export default function ConstitutionalGateOrchestrator({ children, testid = "carelogix-gate-orchestrator", scope = "public" }) {
  const [privacyAck, setPrivacyAck]     = useState(() => isPrivacyAcknowledged());
  const [oracleAck, setOracleAck]       = useState(() => isAcknowledgedV3Local(null));
  const [oracleAckChecked, setOracleAckChecked] = useState(false);
  const [memberIntent, setMemberIntent] = useState(() => (scope === "adminone" ? "adminone" : getMembershipIntent()));

  // Phase 40A — When the user is already an authenticated paying member we
  // skip Gate 3 (Membership Selection). Their tier IS their intent.
  const { loading: sessionLoading, member: sessionMember, isAuthenticated: memberAuthenticated } = useOracleMemberSession();

  // Async server validation of the ORACLE acknowledgement (versioned).
  const validateServer = useCallback(async () => {
    try {
      const res = await checkAcknowledgement(scope);
      setOracleAck(Boolean(res?.acknowledged));
    } catch {
      // network issue → keep local fast-path verdict
    } finally {
      setOracleAckChecked(true);
    }
  }, [scope]);

  useEffect(() => { validateServer(); }, [validateServer]);

  // Periodic re-validation: catches mid-session version bumps from the server.
  useEffect(() => {
    const id = setInterval(validateServer, RECHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [validateServer]);

  useEffect(() => {
    function syncStorage(ev) {
      if (!ev || ev.key === null || ev.key === "carelogix_privacy_safety_ack_v1") setPrivacyAck(isPrivacyAcknowledged());
      if (!ev || ev.key === null || ev.key === "oracle_acknowledged_v3" || ev.key === "oracle_acknowledged_v1" || ev.key === "oracle_acknowledged_version") {
        setOracleAck(isAcknowledgedV3Local(null));
      }
      if (!ev || ev.key === null || ev.key === "carelogix_membership_intent_v1") setMemberIntent(scope === "adminone" ? "adminone" : getMembershipIntent());
    }
    function syncPrivacy(ev) { setPrivacyAck(Boolean(ev?.detail?.value ?? isPrivacyAcknowledged())); }
    function syncOracleV3(ev){ setOracleAck(Boolean(ev?.detail?.value ?? isAcknowledgedV3Local(null))); }
    function syncMember(ev)  { setMemberIntent(ev?.detail?.value ?? (scope === "adminone" ? "adminone" : getMembershipIntent())); }
    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncStorage);
      window.addEventListener("carelogix:privacy-ack-changed", syncPrivacy);
      window.addEventListener("oracle:acknowledged-v3-changed", syncOracleV3);
      window.addEventListener("carelogix:membership-intent-changed", syncMember);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", syncStorage);
        window.removeEventListener("carelogix:privacy-ack-changed", syncPrivacy);
        window.removeEventListener("oracle:acknowledged-v3-changed", syncOracleV3);
        window.removeEventListener("carelogix:membership-intent-changed", syncMember);
      }
    };
  }, [scope]);

  const onPrivacyAccept = useCallback(() => setPrivacyAck(true), []);
  const onOracleAccept  = useCallback(() => {
    setOracleAck(true);
    setOracleAckChecked(true);
  }, []);
  const onMemberPick    = useCallback((intent) => setMemberIntent(intent || "guest"), []);

  if (scope !== "adminone" && !privacyAck) {
    return <CareLogixPrivacyGate onAccept={onPrivacyAccept} testid={`${testid}-gate1`} />;
  }
  // For ORACLE acknowledgement we wait one tick for server validation only when
  // local has *no* prior ack (avoids flashing the gate to returning users).
  // AdminOne operators authenticate via a stronger JWT (scope=adminone, role=super_admin)
  // and do NOT need to clear the consumer-facing ORACLE risk acknowledgement.
  if (scope !== "adminone" && !oracleAck) {
    return <OracleAcknowledgementGateV3 onAccept={onOracleAccept} testid={`${testid}-gate2-v3`} scope={scope} />;
  }
  // Phase 40A — Authenticated paying members never see the membership-
  // selection gate. Their tier IS their membership intent. While the
  // session is still resolving we hold the gate so we never flash Gate 3
  // to a logged-in member.
  if (scope !== "adminone" && !memberIntent && sessionLoading) {
    return (
      <div data-testid={`${testid}-session-check`}
           style={{minHeight:"40vh", display:"flex", alignItems:"center", justifyContent:"center", color:"#d4d4aa", fontFamily:"'Inter',system-ui,sans-serif"}}>
        Checking member access…
      </div>
    );
  }
  if (scope !== "adminone" && !memberIntent && memberAuthenticated) {
    try {
      const t = sessionMember?.tier || "member";
      localStorage.setItem("carelogix_membership_intent_v1", t);
    } catch { /* ignore */ }
    return children;
  }
  if (scope !== "adminone" && !memberIntent) {
    return <MembershipSelectionGate onAccept={onMemberPick} testid={`${testid}-gate3`} />;
  }
  return children;
}
